import React from 'react';
// Feature flag for AM/PM
// import { localeData } from 'utils/DateUtils';
import classnames from 'classnames';
import moment from 'moment';
import { getTimeRange, isSameDay, range } from './utils';

// 🔧 Utils

function useDateTimePicker({
  calendarType,

  disabled,

  onChange,

  defaultDate,
  minDate,
  maxDate,
  beginDate,
  endDate,
}) {
  // 🔄 State
  const [openCalendar, setOpenCalendar] = React.useState(false);
  const [selectedDate, setSelectedDate] = React.useState(defaultDate);
  const [hoveredDate, setHoveredDate] = React.useState(null);

  React.useEffect(() => {
    setSelectedDate(defaultDate);
  }, [defaultDate]);

  // Feature flag / AM-PM
  // const usesAmPm = localeData.usesAmPm();
  const usesAmPm = false;
  const timeRange = getTimeRange(selectedDate);

  const isMinDay = minDate && isSameDay(minDate, selectedDate);
  const isMaxDay = maxDate && isSameDay(maxDate, selectedDate);
  const canSelectAm = usesAmPm && (!isMinDay || (isMinDay && getTimeRange(minDate) !== 'pm'));
  const canSelectPm = usesAmPm && (!isMaxDay || (isMaxDay && getTimeRange(maxDate) !== 'am'));

  /* Hours */
  /* ===== */
  let OptionsHours = range(0, 23).map((hour) => {
    // Disable conditions
    const tooEarly = isMinDay && hour < minDate.getHours();
    const tooLate = isMaxDay && hour > maxDate.getHours();

    // Format
    const hourAmPm = hour % 12 || 12;
    const hourToDisplay = usesAmPm ? hourAmPm : hour;

    return (
      <option
        key={hour}
        disabled={tooEarly || tooLate}
        value={hour}
      >
        {hourToDisplay}
      </option>
    );
  });

  if (usesAmPm) OptionsHours = OptionsHours.slice(timeRange === 'am' ? 0 : 12, timeRange === 'am' ? 12 : 24);

  /* Minutes */
  /* ======= */
  const isMinHour = isMinDay && minDate.getHours() === selectedDate.getHours();
  const isMaxHour = isMaxDay && maxDate.getHours() === selectedDate.getHours();

  const OptionsMinutes = range(0, 59).map((minute) => {
    // Disable conditions
    const minuteBeforeMinDateMinute = isMinHour && minute <= minDate.getMinutes();
    const minuteAfterMaxDateMinute = isMaxHour && minute >= maxDate.getMinutes();

    // Format
    const minuteToDisplay = String(minute).padStart(2, '0');

    return <option key={minute} disabled={(minuteBeforeMinDateMinute) || (minuteAfterMaxDateMinute)} value={minute}>{minuteToDisplay}</option>;
  });

  /* HANDLERS */
  /* ======== */

  const handleSelectedDateChange = (dateMoment) => {
    let newSelectedDate = dateMoment.toDate();

    // If we try to select min or max date, we must be sure date/hour already selected are not before or after date/hour of min or max date
    if (minDate && newSelectedDate < minDate) {
      newSelectedDate = new Date(minDate);
      if (calendarType === 'end') newSelectedDate.setMinutes(newSelectedDate.getMinutes() - 1);
    }

    if (maxDate && newSelectedDate > maxDate) {
      newSelectedDate = new Date(maxDate);
      if (calendarType === 'begin') newSelectedDate.setMinutes(newSelectedDate.getMinutes() + 1);
    }

    setSelectedDate(newSelectedDate);
  };

  const handleSelectHour = (e) => {
    const newSelectedDate = new Date(selectedDate);
    const newSelectedHour = e.target.value;
    newSelectedDate.setHours(newSelectedHour);
    setSelectedDate(newSelectedDate);
  };

  const handleSelectMinute = (e) => {
    const newSelectedDate = new Date(selectedDate);
    const newSelectedMinute = e.target.value;
    newSelectedDate.setMinutes(newSelectedMinute);
    setSelectedDate(newSelectedDate);
  };

  const handleSelectTimeRange = (e) => {
    const selectedTimeRange = e.target.value;
    setSelectedDate((prevDate) => {
      if (timeRange !== selectedTimeRange) {
        if (selectedTimeRange === 'am') {
          const selectedDateUpdated = new Date(prevDate);
          selectedDateUpdated.setHours(prevDate.getHours() - 12);
          return selectedDateUpdated;
        }

        if (selectedTimeRange === 'pm') {
          const selectedDateUpdated = new Date(prevDate);
          selectedDateUpdated.setHours(prevDate.getHours() + 12);
          return selectedDateUpdated;
        }
      }
    });
  };

  const handleResetDate = () => {
    setSelectedDate(defaultDate || new Date());
    onChange(null);
  };

  const handleOpenCalendar = () => {
    if (!disabled) setOpenCalendar(true);
  };

  function renderDay(day, calendarSelectedDate, dayInCurrentMonth, dayComponent) {
    const disableRangeSelection = !(beginDate || endDate) || (beginDate && !endDate && calendarType === 'begin') || (endDate && !beginDate && calendarType === 'end');
    const isBeforeMinDate = (minDate && day.isBefore(moment(minDate).subtract(1, 'days')));
    const isAfterMaxDate = (maxDate && day.isAfter(moment(maxDate).add(1, 'days')));
    const outOfRange = isBeforeMinDate || isAfterMaxDate;

    // ❌ Early return
    if (disableRangeSelection || outOfRange || !dayInCurrentMonth) return dayComponent;

    // ✅ We enable highlights effects

    const isBeginDate = beginDate && isSameDay(beginDate, day.toDate());
    const isEndDate = endDate && isSameDay(endDate, day.toDate());
    const isHoveredDate = hoveredDate && hoveredDate.isSame(day);
    const isSelectedDate = selectedDate && isSameDay(selectedDate, day.toDate());

    let highlightedDtp;
    let shiny;
    let forceDefault = false;
    const hoverMode = hoveredDate;
    const persistantMode = !hoverMode && ((beginDate && selectedDate && !isSameDay(beginDate, selectedDate)) || (endDate && selectedDate && !isSameDay(endDate, selectedDate)));

    let atTheHighlightLeft;
    let atTheHighlightRight;

    if (hoverMode) {
      if (calendarType === 'begin') {
        shiny = isEndDate || isHoveredDate;
        forceDefault = isBeginDate || isSelectedDate;
        highlightedDtp = hoveredDate && endDate && day.isBetween(hoveredDate, moment(endDate).subtract(1, 'days'));
        atTheHighlightLeft = (isSelectedDate && !isEndDate && selectedDate >= hoveredDate.toDate()) || isHoveredDate;
        atTheHighlightRight = isEndDate;
      }

      if (calendarType === 'end') {
        shiny = isBeginDate || isHoveredDate;
        forceDefault = isEndDate || isSelectedDate;
        highlightedDtp = beginDate && hoveredDate && day.isBetween(moment(beginDate), hoveredDate);
        atTheHighlightLeft = isBeginDate;
        atTheHighlightRight = (isSelectedDate && !isBeginDate && selectedDate <= hoveredDate.toDate()) || isHoveredDate;
      }
    }

    if (persistantMode) {
      if (calendarType === 'begin') {
        shiny = isEndDate || isSelectedDate;
        highlightedDtp = selectedDate && endDate && day.isBetween(moment(selectedDate), moment(endDate).subtract(1, 'days'));
        atTheHighlightLeft = isSelectedDate;
        atTheHighlightRight = isEndDate;
      }

      if (calendarType === 'end') {
        shiny = isBeginDate || isSelectedDate;
        highlightedDtp = beginDate && selectedDate && day.isBetween(moment(beginDate), moment(selectedDate).subtract(1, 'days'));
        atTheHighlightLeft = isBeginDate;
        atTheHighlightRight = isSelectedDate;
      }
    }

    return (
      <div
        onMouseEnter={() => setHoveredDate(day)}
        onMouseLeave={() => setHoveredDate(null)}
        className={classnames({
          highlightedDtp,
          'at-the-highlight-left': atTheHighlightLeft,
          'at-the-highlight-right': atTheHighlightRight,

          shiny,
          'force-default': forceDefault,
        })}
      >
        {dayComponent}
      </div>
    );
  }

  // ------------- End highlight functions

  return (
    {
      selectedDate,
      setSelectedDate,

      openCalendar,
      setOpenCalendar,

      usesAmPm,
      canSelectAm,
      canSelectPm,
      timeRange,

      OptionsHours,
      OptionsMinutes,

      handleSelectedDateChange,
      handleSelectHour,
      handleSelectMinute,
      handleSelectTimeRange,
      handleResetDate,
      handleOpenCalendar,

      renderDay,
    }
  );
}

export default useDateTimePicker;



// WEBPACK FOOTER //
// ./components/core/DateTimePicker2/useDateTimePicker.js